"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, Layers, ShieldCheck, HardHat, CircleCheck as CheckCircle2, ArrowRight, CalendarCheck, Award } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const sitePhotoQuery = new WP_Query({
  post_type: "site_photo",
  per_page: 20
});
function HeroSitePhotoImage() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="w-full h-full bg-muted animate-pulse" />;
  }
  const photo = posts.find(p => p.title === "New Build Scaffolding Hero Image" || p.customFields?.photo_name === "New Build Scaffolding Hero Image");
  const imageSrc = photo?.customFields?.image || photo?.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/new_build_scaffolding_site.png";
  const altText = photo?.customFields?.alt_text || "Multi-unit residential new build scaffolding with progressive lifts";
  const isDynamic = Boolean(photo?.customFields?.image || photo?.featuredImage);
  return <img src={imageSrc} data-wvc-srcset={isDynamic ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/new_build_scaffolding_site.png-wvc-srcset"} data-wvc-sizes={isDynamic ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/new_build_scaffolding_site.png-wvc-sizes"} alt={altText} loading="lazy" decoding="async" className="w-full h-full object-cover" />;
}
const audiences = [{
  title: "Developers",
  desc: "Predictable access scheduling across entire development phases."
}, {
  title: "Housebuilders",
  desc: "Sequential plot handovers and staged erection aligned with brickwork."
}, {
  title: "Main contractors",
  desc: "Rigorous site compliance, daily tagging, and multi-trade coordination."
}];
const useCases = [{
  icon: Layers,
  title: "Phased plot access",
  desc: "Individual plots and blocks scaffolded sequentially so trades work without delay or site congestion."
}, {
  icon: Building2,
  title: "Multi-unit developments",
  desc: "Standardised, repeatable access solutions tailored to high-density residential and mixed-use sites."
}, {
  icon: HardHat,
  title: "Progressive lifts",
  desc: "Scaffolds raised in planned stages alongside bricklayers, timber framers, and roofing teams."
}];
const accreditations = [{
  name: "NASC",
  detail: "National Access & Scaffolding Confederation"
}, {
  name: "CHAS",
  detail: "Contractors Health and Safety Assessment Scheme"
}];
export default function NewbuildScaffold() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="newbuildscaffoldinghero" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="flex flex-col gap-12">
          {/* Main Hero Header */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <motion.div variants={itemVariants} className="lg:col-span-7 flex flex-col gap-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-border px-3 py-1 font-sans text-xs text-foreground bg-accent/50">
                  New Build & Site Construction
                </Badge>
                <span className="text-xs text-muted-foreground font-sans">
                  London & South East
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
                New build scaffolding
              </h1>

              <p className="text-lg md:text-xl text-muted-foreground font-sans leading-relaxed">
                BMF Scaffolding Ltd provides planned, safety-led scaffolding access for new-build developments and active construction sites across Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the wider South East.
              </p>

              <div className="flex flex-wrap gap-3 pt-2">
                <Button asChild size="lg" className="font-sans">
                  <Link to="/ContactRequestQuotePage">
                    Request site survey
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="font-sans">
                  <Link to="/ProjectsCaseStudiesPage">
                    View completed projects
                  </Link>
                </Button>
              </div>

              {/* Target Audience Bar */}
              <div className="pt-4 border-t border-border">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Tailored access for site teams
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {audiences.map((item, i) => <div key={item.title} data-index={i} className="p-3.5 rounded-lg bg-card border border-border flex flex-col gap-1 shadow-2xs">
                      <span className="font-semibold text-sm text-foreground font-sans">
                        {item.title}
                      </span>
                      <span className="text-xs text-muted-foreground leading-normal">
                        {item.desc}
                      </span>
                    </div>)}
                </div>
              </div>
            </motion.div>

            {/* Right Side Visual & Key Offerings Card */}
            <motion.div variants={itemVariants} className="lg:col-span-5 flex flex-col gap-6">
              <div className="relative rounded-xl overflow-hidden border border-border bg-muted shadow-sm aspect-[4/3] sm:aspect-[16/10] lg:aspect-auto lg:h-72">
                <PostsProvider wp_query={{
                post_type: "site_photo",
                per_page: 20,
                status: ["publish"]
              }}>
                  <HeroSitePhotoImage />
                </PostsProvider>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 text-white p-2 text-xs font-sans">
                  <span className="font-semibold block">Phased access in progress</span>
                  <span className="text-white/80">Staged lifts aligned with brickwork and roofing</span>
                </div>
              </div>

              {/* What BMF Provides Summary Box */}
              <div className="rounded-xl border border-border bg-card p-6 shadow-xs flex flex-col gap-4">
                <h2 className="text-base font-default font-semibold text-foreground flex items-center gap-2">
                  <CalendarCheck className="size-4 text-primary" />
                  What BMF provides on site
                </h2>
                <ul className="space-y-2.5 text-sm text-muted-foreground font-sans">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Phased scaffold access planned around specific build programmes</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Active coordination with groundworkers, bricklayers, carpenters, and roofers</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Safety-led erection, ongoing handover inspections, and clear tagging</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>

          <Separator />

          {/* Use Cases Grid */}
          <div>
            <div className="mb-6">
              <h2 className="text-xl sm:text-2xl font-default font-bold text-foreground">
                Development site access solutions
              </h2>
              <p className="text-sm text-muted-foreground font-sans mt-1">
                Engineered for rapid progress, safe trade sequencing, and clean plot handovers.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {useCases.map((uc, i) => {
              const Icon = uc.icon;
              return <motion.div key={uc.title} variants={itemVariants} data-index={i}>
                    <Card className="h-full border border-border bg-card shadow-xs hover:border-border/80 transition-colors">
                      <CardHeader className="pb-3">
                        <div className="size-10 rounded-md bg-accent flex items-center justify-center text-primary mb-2">
                          <Icon className="size-5" />
                        </div>
                        <CardTitle className="text-lg font-default font-semibold text-card-foreground">
                          {uc.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                          {uc.desc}
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>;
            })}
            </div>
          </div>

          {/* Safety, Experience & Accreditations Callout */}
          <motion.div variants={itemVariants} className="rounded-xl border border-border bg-accent/40 p-6 md:p-8 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="max-w-3xl flex flex-col gap-2">
              <div className="flex items-center gap-2 text-primary">
                <ShieldCheck className="size-5" />
                <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Operational Safety & Accreditations
                </span>
              </div>
              <h3 className="text-lg md:text-xl font-default font-semibold text-foreground">
                Built on 20+ years of hands-on site leadership
              </h3>
              <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                Led by Pete Wood with over two decades of scaffolding management experience, BMF operates with commercial discipline and strict compliance. Every structure is erected by qualified personnel under confirmed safety frameworks.
              </p>
            </div>

            <div className="flex flex-wrap lg:flex-col gap-3 min-w-[240px]">
              {accreditations.map((acc, i) => <div key={acc.name} data-index={i} className="flex items-center gap-3 bg-card border border-border rounded-lg px-3.5 py-2.5 shadow-2xs w-full sm:w-auto lg:w-full">
                  <Award className="size-4 text-primary shrink-0" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-xs text-foreground font-sans">
                      {acc.name}
                    </span>
                    <span className="text-[11px] text-muted-foreground font-sans">
                      {acc.detail}
                    </span>
                  </div>
                </div>)}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}